'use client';

import { createContext, useContext, useState, ReactNode } from 'react';
import { useCart } from '@/providers/CartProvider';

interface StoreContextType {
  cart: any[];
  cartTotal: number;
  isCartOpen: boolean;
  setCartOpen: (open: boolean) => void;
  removeFromCart: (id: string) => Promise<void>;
  updateQuantity: (id: string, quantity: number) => void;
}

const StoreContext = createContext<StoreContextType | undefined>(undefined);

export function useStore() {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within StoreProvider');
  }
  return context;
}

interface StoreProviderProps {
  children: ReactNode;
}

export function StoreProvider({ children }: StoreProviderProps) {
  const [isCartOpen, setCartOpen] = useState(false);
  const { cart: cartData, updateQuantity, removeFromCart } = useCart();

  // Convert CartResponseDto to simple array for CartDrawer
  const cart = cartData?.cartBody?.items?.map((item: any) => ({
    id: item.productVariantId,
    productId: (item.productVariantData as any)?.productId || (item.productVariantData as any)?.product?._id || item.productVariantId,
    productName: (item as any).product?.name || null,
    variantName: item.productVariantData?.name || 'Product',
    name: item.productVariantData?.name || 'Product',
    price: item.productVariantData?.finalPrice || 0,
    quantity: item.quantity,
    image: (item as any).product?.media?.[0]?.file || item.productVariantData?.media?.[0]?.file || '/placeholder.png',
  })) || [];

  const cartTotal = cartData?.subTotal || 0;

  const value: StoreContextType = {
    cart,
    cartTotal,
    isCartOpen,
    setCartOpen,
    removeFromCart,
    updateQuantity,
  };

  return (
    <StoreContext.Provider value={value}>
      {children}
    </StoreContext.Provider>
  );
}
